import wretch from 'wretch';

import { type LoadServerCxModuleOptions } from '@cx-modules/client';
import { configureErrorCatchers } from '@cx-modules/errors';
import { type Repub } from '@cx-modules/types';

import { type MostPopularBetsResponse } from '../../interfaces';

export type MostPopularBetsRequestParams = {
  categoryGroupId?: number;
  categoryGroupIds?: number[];
  eventIds?: number[];
  includeAll?: boolean;
  limit?: number;
  subeventId?: number;
};

export type MostPopularBetsHeaders = {
  apiKey?: string;
  countryCode?: string;
  repub?: Repub;
  subdivisionCode?: string;
};

export const buildPopularBetsUrl = (dataApiEndpoint: string, params: MostPopularBetsRequestParams): string => {
  const searchParams = new URLSearchParams();
  if (params.categoryGroupIds?.length) {
    searchParams.set('categoryGroupIds', params.categoryGroupIds.join(','));
  } else if (params.categoryGroupId != null) {
    searchParams.set('categoryGroupIds', String(params.categoryGroupId));
  }
  if (params.eventIds?.length) searchParams.set('eventIds', params.eventIds.join(','));
  if (params.subeventId != null) searchParams.set('subeventId', String(params.subeventId));
  if (params.limit != null) searchParams.set('limit', String(params.limit));
  if (params.includeAll) searchParams.set('includeAll', 'true');
  return `${dataApiEndpoint}/v1/popular-bets?${searchParams.toString()}`;
};

export const fetchMostPopularBetsData = async (
  dataApiEndpoint: string | undefined,
  params: MostPopularBetsRequestParams,
  { apiKey, countryCode, repub, subdivisionCode }: MostPopularBetsHeaders,
  options?: LoadServerCxModuleOptions,
): Promise<MostPopularBetsResponse | null> => {
  if (!dataApiEndpoint) throw new Error('No API endpoint');

  const url = buildPopularBetsUrl(dataApiEndpoint, params);

  const requestHeaders: Record<string, string> = {
    accept: 'application/json',
    'content-type': 'application/json',
  };
  if (apiKey) requestHeaders['api-key'] = apiKey;
  if (repub) requestHeaders['repub'] = repub.toUpperCase();
  if (countryCode) requestHeaders['country-code'] = countryCode.toUpperCase();
  if (subdivisionCode) requestHeaders['subdivision-code'] = subdivisionCode.toUpperCase();

  const client = configureErrorCatchers(wretch(url, { headers: requestHeaders }), options);

  try {
    return await client.get().json<MostPopularBetsResponse>();
  } catch (error) {
    if (options?.logger) options.logger.error('Error fetching most-popular-bets data', error);
    return null;
  }
};

export const getMostPopularBetsCacheKey = (params: MostPopularBetsRequestParams) => {
  return ['most-popular-bets', params];
};
